import { dateToYmd, formatDate } from '@/filters/date';
import {
  EditingPayment,
  OrderPayment,
  ResultChangedInstallments,
  ScheduleChanges,
} from '../types';

/**
 * Converts the due date from the server to a local date with the same Y/M/D
 * Otherwise if you do new Date('2025-07-21T00:00:00Z') for a visitor who is in Vancouver,
 * the date picker will show the local date of 2025-07-20
 */
export function dueDateToLocalDate(utcIsoDate: string) : Date {
  const ymd = utcIsoDate.substring(0, 'YYYY-MM-DD'.length);
  const [year, month, day] = ymd.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  return date;
}

// Converts the changes we store in the frontend to the final payload that the API expects
export function getScheduleChangesPayload(originalSchedule: OrderPayment[], allChanges: ScheduleChanges) {
  const updates: Record<string, {
    newAmountInCents?: number;
    newDueDate?: string;
  }> = {};

  for (const installmentId of Object.keys(allChanges)) {
    const changes = allChanges[installmentId];
    const originalInstallment = originalSchedule.find((i) => i.installmentId === installmentId);

    if (!originalInstallment) {
      throw new Error(`Installment ${installmentId} not found in original schedule`);
    }

    if (!wouldChangeInstallment(originalInstallment, changes)) {
      continue;
    }

    updates[installmentId] = changes;
    // We store the due date in the URL in YYYY-MM-DD format, but the server expects UtcIso format
    // so we convert make our dates match that and be at 0
    if (changes.newDueDate) {
      updates[installmentId].newDueDate = new Date(changes.newDueDate).toISOString();
    }
  }

  return updates;
}

/**
 * This confirms that the listed change will actual result in the installment being different
 * EG: if you say "change installment X amount to $100" but the installment is already $100,
 * then the proposed changed wouldn't actually change anything
 */
function wouldChangeInstallment(originalInstallment: OrderPayment, proposedChanges: ScheduleChanges[string]) {
  if (proposedChanges.newAmountInCents !== undefined && proposedChanges.newAmountInCents !== originalInstallment.amountInCents) {
    return true;
  }
  if (proposedChanges.newDueDate !== undefined && dateToYmd(proposedChanges.newDueDate) !== dateToYmd(originalInstallment.dueDate)) {
    return true;
  }
  return false;
}

/**
 * This returns the string that will be stored in the URL to show changes
 */
export function scheduleChangesToString(originalSchedule: OrderPayment[], updates: ScheduleChanges): string {
  const installments: string[] = [];
  const originals = new Map<string, OrderPayment>(originalSchedule.map((os) => [os.installmentId, os]));
  for (const [installmentId, update] of Object.entries(updates)) {
    const changes: string[] = [];
    const orig = originals.get(installmentId);
    if (!orig) {
      continue;
    }
    if (update.newAmountInCents && update.newAmountInCents !== orig.amountInCents) {
      changes.push(`a:${update.newAmountInCents}`);
    }
    try {
      const newDueDate = update.newDueDate ? dateToYmd(update.newDueDate) : undefined;
      if (newDueDate && newDueDate !== dateToYmd(orig.dueDate)) {
        changes.push(`d:${newDueDate}`);
      }
    } catch (error) {
      console.error('Error converting due date to YMD', error);
    }
    if (changes.length === 0) {
      continue;
    }
    installments.push(`${installmentId}=${changes.join(',')}`);
  }
  return installments.sort().join('|');
}

export function stringToScheduleChanges(encoded: string): ScheduleChanges {
  if (!encoded) {
    return {};
  }
  const installments = encoded.split('|');
  const updates: ScheduleChanges = {};
  for (const installment of installments) {
    const [installmentId, changes] = installment.split('=');
    if (!installmentId || !changes) {
      continue;
    }
    const changePairs = changes.split(',');
    for (const change of changePairs) {
      const [key, value] = change.split(':');
      updates[installmentId] = updates[installmentId] || {};
      if (key === 'a') {
        if (/^\d+$/.test(value)) {
          updates[installmentId].newAmountInCents = parseInt(value, 10);
        }
      } else if (key === 'd') {
        try {
          const newDate = dateToYmd(value);
          if (newDate) {
            updates[installmentId].newDueDate = value;
          }
        } catch (error) {
          console.error(`Error converting string "${value}" due date to YMD`, error);
        }
      }
    }
  }
  return updates;
}

// This returns the version of the schedule that the user is editing.
// That means that it looks at the list of changes that are being made and applies them
// It also converts types to those that the UI expects (ie: cents to dollars for the amount textbox)
export function getEditingSchedule(
  schedule: OrderPayment[],
  allLocalChanges: ScheduleChanges,
  resultChanges: ResultChangedInstallments = {},
) : EditingPayment[] {
  const editedCopy = schedule.map((payment) => {
    const editing : EditingPayment = {
      isEditable: payment.isEditable,
      dueDate: dueDateToLocalDate(payment.dueDate),
      amountInCents: payment.amountInCents,
      currency: payment.currency,
      installmentId: payment.installmentId,
      formattedDueDate: formatDate(payment.dueDate, 'MMM dd, yyyy'),
      formattedStatus: payment.formattedStatus,
      hasChanges: false,
      changesDiscarded: false,
    };

    const installmentLocalChanges = allLocalChanges[payment.installmentId];
    const installmentResultChanges = resultChanges[payment.installmentId];

    // If there are changes in the result, those take precedence over your local changes.
    // EG you typed in $1,000 for an installment and the server recalculated it to $995, so we want to show $995
    if (installmentResultChanges) {
      if (installmentResultChanges.amountInCents?.newAmountInCents !== undefined) {
        editing.amountInCents = installmentResultChanges.amountInCents.newAmountInCents;
        editing.hasChanges = true;
        editing.changesDiscarded = installmentResultChanges.amountInCents.changeDiscarded;
      }
      if (installmentResultChanges.dueDate?.newDueDate !== undefined) {
        editing.dueDate = dueDateToLocalDate(installmentResultChanges.dueDate.newDueDate);
        editing.hasChanges = true;
      }
      return editing;
    }

    if (!installmentLocalChanges) {
      return editing;
    }
    if ('newAmountInCents' in installmentLocalChanges && installmentLocalChanges.newAmountInCents !== undefined) {
      editing.amountInCents = installmentLocalChanges.newAmountInCents;
      editing.hasChanges = true;
    }
    if ('newDueDate' in installmentLocalChanges && installmentLocalChanges.newDueDate !== undefined) {
      editing.dueDate = dueDateToLocalDate(installmentLocalChanges.newDueDate);
      editing.hasChanges = true;
    }

    return editing;
  });
  return editedCopy;
}
